import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Highlighter } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';

import { DeleteHighlightButton } from '@/components/DeleteHighlightButton';
import { EngagementRow } from '@/components/EngagementRow';
import { HighlightSourceCard } from '@/components/HighlightSourceCard';
import { NoteContent } from '@/components/NoteContent';
import { ProfileSnippet } from '@/components/ProfileSnippet';
import { Card, CardContent } from '@/components/ui/card';
import type { EngagementStats } from '@/hooks/useEngagement';
import type { SourceEventMap } from '@/hooks/useHighlightSources';
import { highlightPath, primarySource, sourceKey, type Highlight } from '@/lib/highlights';
import { relativeTime } from '@/lib/relativeTime';
import { cn } from '@/lib/utils';

const LONG_QUOTE_CHARS = 500;

/** Quote text with its occurrence inside the `context` marked, when available. */
function QuoteWithContext({ quote, context, serif }: { quote: string; context?: string; serif?: boolean }) {
  const [expanded, setExpanded] = useState(false);

  const parts = useMemo(() => {
    if (!context || !quote) return null;
    const index = context.indexOf(quote);
    if (index === -1) return null;
    return {
      before: context.slice(0, index),
      match: context.slice(index, index + quote.length),
      after: context.slice(index + quote.length),
    };
  }, [context, quote]);

  const isLong = quote.length > LONG_QUOTE_CHARS;
  const clamped = isLong && !expanded;

  if (parts) {
    return (
      <p className={cn('leading-relaxed', serif && 'font-serif text-lg', clamped && 'line-clamp-[8]')}>
        <span className="text-muted-foreground/80">{parts.before}</span>
        <mark className="hl-mark font-medium">{parts.match}</mark>
        <span className="text-muted-foreground/80">{parts.after}</span>
        {isLong && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="ml-2 text-sm text-primary font-medium hover:underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <p className={cn('leading-relaxed', serif && 'font-serif text-lg', clamped && 'line-clamp-[8]')}>
        {quote}
        {isLong && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="ml-2 text-sm text-primary font-medium hover:underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </p>
      {context && !clamped && (
        <p className="text-sm text-muted-foreground/80 leading-relaxed line-clamp-4">{context}</p>
      )}
    </div>
  );
}

interface HighlightCardProps {
  highlight: Highlight;
  sources?: SourceEventMap;
  sourcesLoading?: boolean;
  /** Engagement stats from the batched useEngagement query. */
  stats?: EngagementStats;
  className?: string;
}

/** A single NIP-84 highlight: quote, context, source, and who marked it. */
export function HighlightCard({ highlight, sources, sourcesLoading, stats, className }: HighlightCardProps) {
  const source = primarySource(highlight.sources);
  const sourceEvent = source ? sources?.get(sourceKey(source)) : undefined;

  const commentEvent: NostrEvent | undefined = useMemo(
    () => (highlight.comment ? { ...highlight.event, content: highlight.comment } : undefined),
    [highlight],
  );

  const quoteBody = highlight.quote ? (
    <QuoteWithContext quote={highlight.quote} context={highlight.context} serif={!highlight.comment} />
  ) : null;

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardContent className="p-4 sm:p-5 space-y-3">
        {/* Highlighter attribution */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <ProfileSnippet pubkey={highlight.event.pubkey} />
          <span className="flex items-center gap-1 shrink-0">
            <Highlighter className="size-3.5 text-primary" />
            highlighted
          </span>
          <Link
            to={highlightPath(highlight)}
            className="shrink-0 rounded-sm hover:underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title="Open permalink"
          >
            <time dateTime={new Date(highlight.event.created_at * 1000).toISOString()}>
              {relativeTime(highlight.event.created_at)}
            </time>
          </Link>
          <span className="ml-auto">
            <DeleteHighlightButton highlight={highlight} />
          </span>
        </div>

        {/* Quote-highlight commentary */}
        {commentEvent && <NoteContent event={commentEvent} className="text-[15px]" />}

        {/* The quote itself */}
        {quoteBody && (
          <div
            className={cn(
              'border-l-4 border-primary/70 pl-4 py-1',
              highlight.comment && 'rounded-r-lg bg-muted/50 pr-4 py-3',
            )}
          >
            {quoteBody}
          </div>
        )}

        {/* Source */}
        {source && (
          <HighlightSourceCard source={source} event={sourceEvent} loading={sourcesLoading} />
        )}

        {/* Engagement */}
        <EngagementRow highlight={highlight} stats={stats} />
      </CardContent>
    </Card>
  );
}
